import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input, Select } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

interface PromoCodeRow {
  id: string;
  code: string;
  name: string;
  discount_type: 'percentage' | 'fixed_amount' | 'fee_percentage' | 'fee_fixed';
  discount_value: number;
  max_uses: number;
  current_uses: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

const DISCOUNT_TYPES = ['percentage', 'fixed_amount', 'fee_percentage', 'fee_fixed'] as const;

export function PromoCodePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');
  const [formDiscountType, setFormDiscountType] = useState<(typeof DISCOUNT_TYPES)[number]>('percentage');
  const [formDiscountValue, setFormDiscountValue] = useState('');
  const [formMaxUses, setFormMaxUses] = useState('0');
  const [formExpiresAt, setFormExpiresAt] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCode, setEditCode] = useState('');
  const [editName, setEditName] = useState('');
  const [editDiscountType, setEditDiscountType] = useState<(typeof DISCOUNT_TYPES)[number]>('percentage');
  const [editDiscountValue, setEditDiscountValue] = useState('');
  const [editMaxUses, setEditMaxUses] = useState('0');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editExpiresAt, setEditExpiresAt] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: qk.promoCodes(search),
    queryFn: () =>
      callFunction<PromoCodeRow[]>('admin-promo-crud', {
        query: search ? { search } : undefined,
      }),
  });

  const createPromo = useMutation({
    mutationFn: (input: {
      code: string;
      name: string;
      discount_type: string;
      discount_value: number;
      max_uses: number;
      expires_at?: string;
    }) => callFunction('admin-promo-crud', { body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.promoCodes('') });
      setFormCode('');
      setFormName('');
      setFormDiscountValue('');
      setFormMaxUses('0');
      setFormExpiresAt('');
    },
  });

  const updatePromo = useMutation({
    mutationFn: (input: {
      id: string;
      code?: string;
      name?: string;
      discount_type?: string;
      discount_value?: number;
      max_uses?: number;
      is_active?: boolean;
      expires_at?: string | null;
    }) => callFunction('admin-promo-crud', { method: 'PATCH', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.promoCodes('') });
      setEditingId(null);
    },
  });

  const deletePromo = useMutation({
    mutationFn: (id: string) =>
      callFunction('admin-promo-crud', { method: 'DELETE', query: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.promoCodes('') }),
  });

  if (isLoading) return <LoadingScreen />;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const codes = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('promoCodes.createTitle')}</CardTitle>
        </CardHeader>

        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const dv = Number(formDiscountValue);
            if (!dv || dv <= 0) return;
            if (!formCode.trim() || !formName.trim()) return;
            createPromo.mutate({
              code: formCode,
              name: formName,
              discount_type: formDiscountType,
              discount_value: dv,
              max_uses: Math.max(0, Number(formMaxUses) || 0),
              expires_at: formExpiresAt || undefined,
            });
          }}
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-text-muted">{t('promoCodes.code')}</label>
            <Input
              placeholder="SUMMER10"
              value={formCode}
              onChange={(e) => setFormCode(e.target.value.toUpperCase())}
              className="min-w-28"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-text-muted">{t('promoCodes.name')}</label>
            <Input
              placeholder={t('promoCodes.namePlaceholder')}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="min-w-36"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-text-muted">{t('promoCodes.discountType')}</label>
            <Select
              value={formDiscountType}
              onChange={(v) => setFormDiscountType(v as (typeof DISCOUNT_TYPES)[number])}
              options={DISCOUNT_TYPES.map((dt) => ({ value: dt, label: t(`promoCodes.dt.${dt}`) }))}
              className="min-w-32"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-text-muted">{t('promoCodes.discountValue')}</label>
            <Input
              type="number"
              step="0.0001"
              min="0.0001"
              placeholder="0.10"
              value={formDiscountValue}
              onChange={(e) => setFormDiscountValue(e.target.value)}
              className="min-w-24"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-text-muted">{t('promoCodes.maxUses')}</label>
            <Input
              type="number"
              min="0"
              placeholder="0"
              value={formMaxUses}
              onChange={(e) => setFormMaxUses(e.target.value)}
              className="min-w-20"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-text-muted">{t('promoCodes.expiresAt')}</label>
            <Input
              type="date"
              value={formExpiresAt}
              onChange={(e) => setFormExpiresAt(e.target.value)}
              className="min-w-32"
            />
          </div>
          <Button type="submit" isLoading={createPromo.isPending} className="self-end">
            {t('common.save')}
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('promoCodes.listTitle')}</CardTitle>
          <div className="mt-3">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>
        </CardHeader>

        {codes.length === 0 ? (
          <EmptyState title={t('promoCodes.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-text-muted">
                  <th className="px-2 py-2">{t('promoCodes.code')}</th>
                  <th className="px-2 py-2">{t('promoCodes.name')}</th>
                  <th className="px-2 py-2">{t('promoCodes.discountType')}</th>
                  <th className="px-2 py-2">{t('promoCodes.discountValue')}</th>
                  <th className="px-2 py-2">{t('promoCodes.uses')}</th>
                  <th className="px-2 py-2">{t('promoCodes.expiresAt')}</th>
                  <th className="px-2 py-2">{t('promoCodes.status')}</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {codes.map((promo) => (
                  <tr key={promo.id} className="border-t border-border">
                    {editingId === promo.id ? (
                      <>
                        <td className="px-2 py-2">
                          <Input
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                            className="min-w-20"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="min-w-28"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select
                            value={editDiscountType}
                            onChange={(v) => setEditDiscountType(v as (typeof DISCOUNT_TYPES)[number])}
                            options={DISCOUNT_TYPES.map((dt) => ({ value: dt, label: t(`promoCodes.dt.${dt}`) }))}
                            className="min-w-28"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.0001"
                            value={editDiscountValue}
                            onChange={(e) => setEditDiscountValue(e.target.value)}
                            className="min-w-20"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            min="0"
                            value={editMaxUses}
                            onChange={(e) => setEditMaxUses(e.target.value)}
                            className="min-w-20"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="date"
                            value={editExpiresAt}
                            onChange={(e) => setEditExpiresAt(e.target.value)}
                            className="min-w-28"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select
                            value={editIsActive ? 'active' : 'inactive'}
                            onChange={(v) => setEditIsActive(v === 'active')}
                            options={[
                              { value: 'active', label: t('promoCodes.active') },
                              { value: 'inactive', label: t('promoCodes.inactive') },
                            ]}
                            className="min-w-24"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              isLoading={updatePromo.isPending}
                              onClick={() => {
                                const dv = Number(editDiscountValue);
                                if (!dv || dv <= 0) return;
                                updatePromo.mutate({
                                  id: promo.id,
                                  code: editCode,
                                  name: editName,
                                  discount_type: editDiscountType,
                                  discount_value: dv,
                                  max_uses: Math.max(0, Number(editMaxUses) || 0),
                                  is_active: editIsActive,
                                  expires_at: editExpiresAt || null,
                                });
                              }}
                            >
                              {t('common.save')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-2 font-mono font-semibold text-primary">{promo.code}</td>
                        <td className="px-2 py-2">{promo.name}</td>
                        <td className="px-2 py-2 text-xs">{t(`promoCodes.dt.${promo.discount_type}`)}</td>
                        <td className="px-2 py-2 font-mono">
                          {promo.discount_type === 'percentage' || promo.discount_type === 'fee_percentage'
                            ? `${(Number(promo.discount_value) * 100).toFixed(1)}%`
                            : `${Number(promo.discount_value).toFixed(2)}`}
                        </td>
                        <td className="px-2 py-2 text-xs text-text-muted">
                          {promo.max_uses > 0
                            ? `${promo.current_uses} / ${promo.max_uses}`
                            : `${promo.current_uses} (∞)`}
                        </td>
                        <td className="px-2 py-2 text-xs text-text-muted">
                          {promo.expires_at
                            ? new Date(promo.expires_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-2 py-2">
                          <span
                            className={`rounded-pill px-2 py-0.5 text-xs font-medium ${
                              promo.is_active
                                ? 'bg-success/12 text-success'
                                : 'bg-danger/10 text-danger'
                            }`}
                          >
                            {promo.is_active ? t('promoCodes.active') : t('promoCodes.inactive')}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingId(promo.id);
                                setEditCode(promo.code);
                                setEditName(promo.name);
                                setEditDiscountType(promo.discount_type);
                                setEditDiscountValue(String(promo.discount_value));
                                setEditMaxUses(String(promo.max_uses));
                                setEditIsActive(promo.is_active);
                                setEditExpiresAt(promo.expires_at ? promo.expires_at.slice(0, 10) : '');
                              }}
                            >
                              {t('common.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm(t('promoCodes.deleteConfirm'))) {
                                  deletePromo.mutate(promo.id);
                                }
                              }}
                            >
                              {t('common.delete')}
                            </Button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
